import axios from "axios";

import Vue from "vue";
import VueAxios from "vue-axios";

import router from "@/router";
import store from "@/store";

import { JWTService, UserService } from "@/storage";
import { APIError, APIResponse } from "./utils";
import { LOGOUT } from "@/store/auth.module";

const CONTENT_TYPE = "application/json";
const PUBLIC_URLS = [
  "admin/auth/refresh-token",
  "admin/auth/verify-token",
  "admin/auth/login",
  "enumerates/countries",
  "enumerates/currencies",
  "enumerates/genders",
  "enumerates/nationalities",
];

class APIService {
  constructor() {
    let client = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: parseInt(process.env.VUE_APP_TIMEOUT),
      responseType: "json",
      headers: {
        accept: CONTENT_TYPE,
        "content-type": CONTENT_TYPE,
      },
      paramsSerializer: (params) => {
        const parts = [];
        for (const key of Object.keys(params)) {
          const value = params[key];
          if (value === null || value === undefined) continue;
          if (Array.isArray(value)) {
            value.forEach((v) =>
              parts.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(v)}`
              )
            );
          } else {
            parts.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            );
          }
        }
        return parts.join("&");
      },
    });

    client.interceptors.request.use(
      (config) => {
        this.setAuthHeader(config);

        return config;
      },
      (error) => {
        return Promise.reject(new APIError(error));
      }
    );

    client.interceptors.response.use(
      (response) => {
        return Promise.resolve(new APIResponse(response));
      },
      (error) => {
        if (!error.response) {
          return Promise.reject(new APIError(error));
        }
        if (error.response.status !== 401) {
          return Promise.reject(new APIError(error));
        }
        if (error.config.url === "admin/auth/login") {
          return Promise.reject(new APIError(error));
        }

        // Logout user if token refresh didn't work.
        if (error.config.url === "admin/auth/refresh-token") {
          JWTService.removeToken();
          UserService.removeUser();
          store.dispatch(LOGOUT);

          const currentPath = router.currentRoute.fullPath;
          if (currentPath !== "/login") {
            router.replace({
              name: "login",
              query: { next: currentPath },
            });
          }

          return Promise.reject(new APIError(error));
        }

        let payload = {
          token: JWTService.getToken(),
        };

        return client
          .post("admin/auth/refresh-token", payload)
          .then(({ data }) => {
            const config = error.config;

            JWTService.setToken(data.data.token);

            this.setAuthHeader(config);

            return new Promise((resolve, reject) => {
              client
                .request(config)
                .then((response) => {
                  resolve(new APIResponse(response));
                })
                .catch((error) => {
                  reject(new APIError(error));
                });
            });
          })
          .catch((error) => {
            return Promise.reject(new APIError(error));
          });
      }
    );

    Vue.use(VueAxios, client);
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   */
  setAuthHeader(config) {
    let token = JWTService.getToken();

    if (token && !PUBLIC_URLS.includes(config.url)) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
}

export default APIService;
